import { useEffect, useState, type ReactElement } from 'react';
import {
  startStravaOAuth,
  StravaNotConfiguredError,
  StravaRateLimitedError,
  StravaUpstreamError,
  StravaUnauthorizedError,
  useStravaAppConfig,
  useStravaDisconnect,
  useStravaStatus,
  useStravaSync,
  useDeleteStravaAppConfig,
  type StravaAppConfig,
  type StravaAppConfigPutResponse,
  type StravaStatus,
} from '@/lib/strava';
import { ConfirmDialog } from './ConfirmDialog';
import { StravaAppConfigForm } from './StravaAppConfigForm';

/**
 * Página `/ajustes/strava` — tres estados según `status.appConfigured` y
 * `status.connected`:
 *
 *  1. `!appConfigured`               → <StravaAppConfigForm /> (claves Strava).
 *  2. `appConfigured && !connected`  → tarjeta config + CTA "Conectar Strava".
 *  3. `appConfigured && connected`   → tarjeta config + atleta + sync/disconnect.
 *
 * Lee query params al cargar (`?ok=1` ó `?error=...`) para mostrar el
 * resultado del callback OAuth, y los limpia con `replaceState` para que un
 * F5 no muestre el banner otra vez.
 */
export function StravaSettings(): ReactElement {
  const statusQuery = useStravaStatus();
  const appConfigQuery = useStravaAppConfig();
  const syncMutation = useStravaSync();
  const disconnectMutation = useStravaDisconnect();
  const deleteAppConfigMutation = useDeleteStravaAppConfig();

  const [callbackBanner, setCallbackBanner] = useState<
    { kind: 'success' | 'error'; message: string } | null
  >(null);
  const [savedBanner, setSavedBanner] = useState<
    { kind: 'success' | 'warning'; message: string } | null
  >(null);
  const [confirmDisconnectOpen, setConfirmDisconnectOpen] = useState(false);
  const [confirmDeleteConfigOpen, setConfirmDeleteConfigOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(false);

  // Lee y limpia los query params del callback OAuth una sola vez.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const ok = url.searchParams.get('ok');
    const errorCode = url.searchParams.get('error');
    if (!ok && !errorCode) return;

    if (ok === '1') {
      setCallbackBanner({ kind: 'success', message: 'Conectado correctamente con Strava.' });
    } else if (errorCode) {
      setCallbackBanner({ kind: 'error', message: mapOAuthError(errorCode) });
    }

    url.searchParams.delete('ok');
    url.searchParams.delete('error');
    const cleaned = url.pathname + (url.searchParams.toString() ? `?${url.searchParams}` : '') + url.hash;
    window.history.replaceState(window.history.state, '', cleaned);
  }, []);

  function handleConnect() {
    startStravaOAuth();
  }

  function handleSync() {
    syncMutation.mutate(undefined);
  }

  function handleDisconnect() {
    disconnectMutation.mutate(undefined, {
      onSuccess: () => setConfirmDisconnectOpen(false),
    });
  }

  function handleDeleteAppConfig() {
    deleteAppConfigMutation.mutate(undefined, {
      onSuccess: () => {
        setConfirmDeleteConfigOpen(false);
        setEditingConfig(false);
        setSavedBanner({
          kind: 'success',
          message: 'Configuración de la app Strava borrada.',
        });
      },
    });
  }

  // Status manda como fuente principal sobre `appConfigured`/`connected`,
  // pero necesitamos `callbackUrl`, `clientId` y `updatedAt` del app-config.
  const status: StravaStatus | undefined = statusQuery.data;
  const appConfig: StravaAppConfig | undefined = appConfigQuery.data;

  // Si por algún motivo `appConfigured` no viene del status (backend antiguo
  // o estado de carga), usamos el `configured` del app-config como respaldo.
  const appConfigured: boolean =
    status?.appConfigured ?? appConfig?.configured ?? false;
  const connected: boolean = status?.connected ?? false;

  return (
    <section aria-labelledby="strava-title" className="space-y-6">
      <header>
        <h1 id="strava-title" className="text-2xl font-semibold tracking-tight text-slate-900">
          Strava
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Sincroniza tus actividades de Strava para que el coach las analice.
        </p>
      </header>

      {callbackBanner && (
        <div
          role={callbackBanner.kind === 'success' ? 'status' : 'alert'}
          className={
            callbackBanner.kind === 'success'
              ? 'rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900'
              : 'rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800'
          }
        >
          {callbackBanner.message}
        </div>
      )}

      {savedBanner && (
        <div
          role="status"
          className={
            savedBanner.kind === 'success'
              ? 'rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900'
              : 'rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900'
          }
        >
          {savedBanner.message}
        </div>
      )}

      {(statusQuery.isLoading || appConfigQuery.isLoading) && (
        <p role="status" aria-live="polite" className="text-slate-700">
          Cargando estado de Strava…
        </p>
      )}

      {statusQuery.isError && (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          No se pudo cargar el estado de Strava.{' '}
          {statusQuery.error?.message ?? ''}
        </p>
      )}

      {appConfigQuery.isError && !statusQuery.isError && (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          No se pudo cargar la configuración de la app Strava.{' '}
          {appConfigQuery.error?.message ?? ''}
        </p>
      )}

      {/* Estado 1: aún no hay claves configuradas → form directo. */}
      {status && appConfig && !appConfigured && (
        <StravaAppConfigForm
          callbackUrl={appConfig.callbackUrl}
          mode="create"
          onSaved={(response) => {
            setSavedBanner({
              kind: 'success',
              message:
                'Claves guardadas. Ahora conecta tu cuenta de Strava más abajo.',
            });
            if (response.previousConnectionInvalidated) {
              // No ocurre en el estado 1 (no había conexión previa), pero por
              // robustez si el backend lo señala lo respetamos.
              setSavedBanner({
                kind: 'warning',
                message:
                  'Tu conexión anterior se ha invalidado. Vuelve a conectar Strava.',
              });
            }
          }}
        />
      )}

      {/* Estado 2 y 3: hay claves. Pintamos tarjeta de config (siempre) y luego
          la tarjeta de conexión apropiada. */}
      {status && appConfig && appConfigured && (
        <>
          <AppConfigCard
            appConfig={appConfig}
            editing={editingConfig}
            onEdit={() => setEditingConfig(true)}
            onCancelEdit={() => setEditingConfig(false)}
            onAskDelete={() => setConfirmDeleteConfigOpen(true)}
            onSaved={(response) => {
              setEditingConfig(false);
              if (response.previousConnectionInvalidated) {
                setSavedBanner({
                  kind: 'warning',
                  message:
                    'Claves actualizadas. Tu conexión anterior se ha invalidado: vuelve a conectar Strava.',
                });
              } else {
                setSavedBanner({
                  kind: 'success',
                  message: 'Claves actualizadas correctamente.',
                });
              }
            }}
          />

          {connected ? (
            <ConnectedCard
              status={status}
              onSync={handleSync}
              onAskDisconnect={() => setConfirmDisconnectOpen(true)}
              syncIsPending={syncMutation.isPending}
              syncResult={syncMutation.data ?? null}
              syncError={syncMutation.error}
              onReconnect={handleConnect}
            />
          ) : (
            <ConnectAccountCard onConnect={handleConnect} />
          )}
        </>
      )}

      <ConfirmDialog
        open={confirmDisconnectOpen}
        title="¿Desconectar Strava?"
        description={
          <>
            Los entrenamientos ya sincronizados se conservan, pero no se
            descargarán nuevos hasta que vuelvas a conectar. Esto no borra
            tus claves de la app.
          </>
        }
        confirmLabel={disconnectMutation.isPending ? 'Desconectando…' : 'Desconectar'}
        destructive
        isPending={disconnectMutation.isPending}
        onConfirm={handleDisconnect}
        onCancel={() => setConfirmDisconnectOpen(false)}
      />

      <ConfirmDialog
        open={confirmDeleteConfigOpen}
        title="¿Borrar configuración de la app?"
        description={
          <>
            Se borrarán tus claves Client ID y Client Secret. Si estabas
            conectado, también se cerrará la sesión OAuth. Tendrás que
            volver a configurar la app para sincronizar.
          </>
        }
        confirmLabel={deleteAppConfigMutation.isPending ? 'Borrando…' : 'Borrar config'}
        destructive
        isPending={deleteAppConfigMutation.isPending}
        onConfirm={handleDeleteAppConfig}
        onCancel={() => setConfirmDeleteConfigOpen(false)}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Card: configuración de la app (Client ID + botones editar/borrar)
// ---------------------------------------------------------------------------

function AppConfigCard({
  appConfig,
  editing,
  onEdit,
  onCancelEdit,
  onAskDelete,
  onSaved,
}: {
  appConfig: StravaAppConfig;
  editing: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onAskDelete: () => void;
  onSaved: (response: StravaAppConfigPutResponse) => void;
}): ReactElement {
  if (editing) {
    return (
      <StravaAppConfigForm
        callbackUrl={appConfig.callbackUrl}
        initialClientId={appConfig.clientId ?? ''}
        mode="edit"
        onSaved={onSaved}
        onCancel={onCancelEdit}
      />
    );
  }

  return (
    <section
      aria-labelledby="strava-app-config-title"
      className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
    >
      <h2
        id="strava-app-config-title"
        className="text-lg font-semibold text-slate-900"
      >
        Configuración de la app
      </h2>
      <dl className="mt-3 space-y-2 text-sm">
        <Row label="Client ID">
          <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-800">
            {appConfig.clientId ?? '—'}
          </code>
        </Row>
        {appConfig.updatedAt && (
          <Row label="Actualizado">
            <span className="text-slate-900">
              {formatDate(appConfig.updatedAt)}
            </span>
          </Row>
        )}
      </dl>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onEdit}
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
        >
          Editar claves
        </button>
        <button
          type="button"
          onClick={onAskDelete}
          className="rounded-md border border-rose-300 bg-white px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50 focus:outline-none focus:ring-2 focus:ring-rose-600 focus:ring-offset-2"
        >
          Borrar config
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Card: conectar cuenta (estado 2 — app configurada pero sin OAuth)
// ---------------------------------------------------------------------------

function ConnectAccountCard({ onConnect }: { onConnect: () => void }): ReactElement {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-lg font-semibold text-slate-900">Conecta tu cuenta</h2>
      <p className="mt-2 text-sm text-slate-700">
        Conectar Strava permite a tri-mentor leer tus actividades para evaluar
        cómo se ajustan a lo planificado. Pedimos los permisos:
      </p>
      <ul className="mt-2 list-disc pl-6 text-sm text-slate-700">
        <li>
          <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">activity:read_all</code>{' '}
          — leer todas tus actividades (incluidas las privadas).
        </li>
        <li>
          <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">profile:read_all</code>{' '}
          — leer tu perfil de atleta.
        </li>
      </ul>
      <p className="mt-2 text-sm text-slate-600">
        Nunca publicamos ni modificamos nada en tu Strava. Puedes desconectar
        en cualquier momento desde esta misma pantalla.
      </p>

      <div className="mt-6">
        <button
          type="button"
          onClick={onConnect}
          className="rounded-md bg-orange-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-600 focus:ring-offset-2"
        >
          Conectar Strava
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Card: conectado (estado 3)
// ---------------------------------------------------------------------------

function ConnectedCard({
  status,
  onSync,
  onAskDisconnect,
  syncIsPending,
  syncResult,
  syncError,
  onReconnect,
}: {
  status: StravaStatus;
  onSync: () => void;
  onAskDisconnect: () => void;
  syncIsPending: boolean;
  syncResult: { syncedCount: number; lastSyncAt: string } | null;
  syncError: Error | null;
  onReconnect: () => void;
}): ReactElement {
  const expired = syncError instanceof StravaUnauthorizedError;
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-lg font-semibold text-slate-900">Strava conectado</h2>
      <dl className="mt-3 space-y-2 text-sm">
        <Row label="Atleta">
          <span className="text-slate-900">
            {status.athleteName ?? 'Sin nombre'}
            {status.athleteId !== undefined && (
              <span className="ml-2 text-xs text-slate-500">#{status.athleteId}</span>
            )}
          </span>
        </Row>
        <Row label="Último sync">
          <span className="text-slate-900">
            {formatLastSync(status.lastSyncAt ?? null)}
            {typeof status.lastSyncCount === 'number' && status.lastSyncAt && (
              <span className="ml-2 text-xs text-slate-500">
                ({status.lastSyncCount} actividad{status.lastSyncCount === 1 ? '' : 'es'})
              </span>
            )}
          </span>
        </Row>
        {status.scopes && (
          <Row label="Permisos">
            <code className="rounded bg-slate-100 px-1 py-0.5 text-xs text-slate-700">
              {status.scopes}
            </code>
          </Row>
        )}
      </dl>

      {syncResult && !syncError && (
        <p
          role="status"
          aria-live="polite"
          className="mt-4 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900"
        >
          Se sincronizaron {syncResult.syncedCount} actividad
          {syncResult.syncedCount === 1 ? '' : 'es'}.
        </p>
      )}

      {syncError && (
        <p role="alert" className="mt-4 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {mapSyncError(syncError)}
        </p>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        {expired ? (
          <button
            type="button"
            onClick={onReconnect}
            className="rounded-md bg-orange-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-600 focus:ring-offset-2"
          >
            Reconectar Strava
          </button>
        ) : (
          <button
            type="button"
            onClick={onSync}
            disabled={syncIsPending}
            aria-busy={syncIsPending}
            className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {syncIsPending ? (
              <span className="inline-flex items-center gap-2">
                <Spinner /> Sincronizando…
              </span>
            ) : (
              'Sincronizar ahora'
            )}
          </button>
        )}
        <button
          type="button"
          onClick={onAskDisconnect}
          className="rounded-md border border-rose-300 bg-white px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50 focus:outline-none focus:ring-2 focus:ring-rose-600 focus:ring-offset-2"
        >
          Desconectar
        </button>
      </div>
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactElement | string }): ReactElement {
  return (
    <div className="grid grid-cols-[8rem_1fr] gap-2 text-sm">
      <dt className="text-slate-600">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Spinner(): ReactElement {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent"
    />
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function mapOAuthError(code: string): string {
  switch (code) {
    case 'missing_state':
      return 'Se perdió la sesión OAuth. Reinicia el flujo.';
    case 'invalid_state':
      return 'Estado OAuth corrupto.';
    case 'state_mismatch':
      return 'Mismatch de estado OAuth, posible CSRF.';
    case 'strava_app_no_configurado':
      return 'Configura primero tus claves Strava antes de conectar.';
    default:
      return `Error al conectar con Strava: ${code}`;
  }
}

function mapSyncError(err: Error): string {
  if (err instanceof StravaUnauthorizedError) {
    return 'Conexión con Strava expirada. Reconecta.';
  }
  if (err instanceof StravaRateLimitedError) {
    return 'Strava limitó las peticiones. Intenta en unos minutos.';
  }
  if (err instanceof StravaUpstreamError) {
    return 'Error en Strava, vuelve a intentarlo.';
  }
  if (err instanceof StravaNotConfiguredError) {
    return `Strava no está configurado. ${err.hint}`;
  }
  return 'No se pudo sincronizar con Strava. Inténtalo de nuevo.';
}

function formatLastSync(iso: string | null): string {
  if (!iso) return 'Nunca';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return iso;
  const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (diffSec < 60) return 'Hace unos segundos';
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `Hace ${diffMin} minuto${diffMin === 1 ? '' : 's'}`;
  const diffHour = Math.round(diffMin / 60);
  if (diffHour < 24) return `Hace ${diffHour} hora${diffHour === 1 ? '' : 's'}`;
  const diffDay = Math.round(diffHour / 24);
  if (diffDay < 30) return `Hace ${diffDay} día${diffDay === 1 ? '' : 's'}`;
  return new Date(iso).toLocaleString('es-ES');
}

function formatDate(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return iso;
  return new Date(iso).toLocaleString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}
